import React, { useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Leaf, Info, SolarPanel, Mail, Menu, X, ArrowRight } from './Icons';
import logo from '../images/logo.png';

const nav = [
  { to: '/', label: 'Home', icon: Leaf },
  { to: '/about', label: 'About', icon: Info },
  { to: '/solar-parks', label: 'Projects', icon: SolarPanel },
  { to: '/contact', label: 'Contact', icon: Mail },
];

function useScrollProgress() {
  const [p, setP] = useState(0);

  useEffect(() => {
    const onScroll = () => {
      const el = document.documentElement;
      const scrollTop = el.scrollTop;
      const height = el.scrollHeight - el.clientHeight;
      const next = height <= 0 ? 0 : (scrollTop / height) * 100;
      setP(next);
    };
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  return p;
}

export default function Navbar() {
  const { pathname } = useLocation();
  const progress = useScrollProgress();
  const [open, setOpen] = useState(false);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  const activeIdx = useMemo(() => nav.findIndex((i) => i.to === pathname), [pathname]);

  return (
    <header className="fixed top-0 left-0 right-0 z-50">
      <div className="mx-auto max-w-6xl px-4">
        <div className="mt-3 rounded-2xl border border-white/10 bg-black/30 backdrop-blur-xl shadow-[0_10px_30px_-15px_rgba(0,0,0,0.8)]">
          <div className="flex items-center justify-between px-3 py-2 md:px-4">
            <Link to="/" className="flex items-center gap-3">
              <img src={logo} alt="Praash Green Energy" className="h-10 w-10 rounded-xl object-contain bg-white/5 p-1" />
              <div className="hidden sm:block">
                <div className="text-sm font-semibold leading-4">Praash Green Energy</div>
                <div className="text-xs text-white/60">Clean Power. Clear Future.</div>
              </div>
            </Link>

            {/* Desktop nav */}
            <nav className="hidden md:block">
              <div className="relative rounded-2xl bg-white/5 p-1">
                {/* Active pill (behind links) */}
                {activeIdx >= 0 && (
                  <div
                    className="pointer-events-none absolute inset-y-1 rounded-xl bg-white/10 transition-all duration-300"
                    style={{
                      left: `calc(${activeIdx} * (100% / ${nav.length}) + 4px)`,
                      width: `calc((100% / ${nav.length}) - 8px)`,
                    }}
                  />
                )}
                <div className="flex items-center gap-1">
                  {nav.map((item) => {
                    const Icon = item.icon;
                    const isActive = pathname === item.to;
                    return (
                      <Link
                        key={item.to}
                        to={item.to}
                        className={
                          "group relative z-10 flex items-center gap-2 rounded-xl px-4 py-2 text-sm transition " +
                          (isActive ? "text-white" : "text-white/70 hover:text-white")
                        }
                      >
                        <Icon className="h-4 w-4 opacity-80 group-hover:opacity-100" />
                        <span>{item.label}</span>
                      </Link>
                    );
                  })}
                </div>
              </div>
            </nav>

            {/* Mobile menu button */}
            <button
              onClick={() => setOpen((s) => !s)}
              className="md:hidden inline-flex items-center justify-center rounded-xl border border-white/10 bg-white/5 p-2 text-white/90 hover:bg-white/10"
              aria-label={open ? 'Close menu' : 'Open menu'}
            >
              {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </button>
          </div>

          {/* Progress bar */}
          <div className="h-[2px] w-full bg-white/5">
            <div className="h-[2px] bg-emerald-400/70" style={{ width: `${progress}%` }} />
          </div>

          {/* Mobile dropdown */}
          {open && (
            <div className="md:hidden overflow-hidden animate-slideDown">
                <div className="px-3 pb-3">
                  <div className="rounded-2xl border border-white/10 bg-white/5 p-2">
                    {nav.map((item) => {
                      const Icon = item.icon;
                      const isActive = pathname === item.to;
                      return (
                        <Link
                          key={item.to}
                          to={item.to}
                          className={
                            "flex items-center justify-between rounded-xl px-3 py-2 text-sm transition " +
                            (isActive ? "bg-white/10 text-white" : "text-white/80 hover:bg-white/10")
                          }
                        >
                          <span className="flex items-center gap-2">
                            <Icon className="h-4 w-4" />
                            {item.label}
                          </span>
                          <ArrowRight className="h-4 w-4 opacity-60" />
                        </Link>
                      );
                    })}
                  </div>
                </div>
            </div>
          )}
        </div>
      </div>
    </header>
  );
}
